// src/pages/admin/BookingManagement.jsx

import React, { useState, useEffect, useCallback } from "react";
import ConfirmationModal from "../../components/admin/ConfirmationModal";
import PaymentVerificationModal from "../../components/admin/PaymentVerificationModal";
import AdminBookingForm from "./AdminBookingForm";
import * as adminApi from "../../services/admin";
import "./BookingManagement.css";

const BookingManagement = () => {
  const [bookings, setBookings] = useState([]);
  const [filteredBookings, setFilteredBookings] = useState([]);
  const [showBookingForm, setShowBookingForm] = useState(false);
  const [editingBooking, setEditingBooking] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [oasisFilter, setOasisFilter] = useState("all");
  const [sourceFilter, setSourceFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [verificationModalOpen, setVerificationModalOpen] = useState(false);
  const [selectedBookingForVerification, setSelectedBookingForVerification] =
    useState(null);
  const [confirmationModal, setConfirmationModal] = useState({
    isOpen: false,
    title: "",
    message: "",
    onConfirm: null,
    confirmText: "Confirm",
    cancelText: "Cancel",
  });

  const fetchBookings = useCallback(async () => {
    try {
      const data = await adminApi.getAllBookings();
      console.log("Bookings data:", data);

      setBookings(data);
    } catch (error) {
      console.error("Error fetching bookings:", error);
      showConfirmationModal("Error", "Failed to fetch bookings", null, "OK");
    }
  }, []);

  useEffect(() => {
    fetchBookings();
  }, [fetchBookings]);

  useEffect(() => {
    let filtered = [...bookings];

    if (statusFilter !== "all") {
      filtered = filtered.filter(
        (b) => b.status === statusFilter,
      );
    }

    if (oasisFilter !== "all") {
      filtered = filtered.filter((b) => b.oasis === oasisFilter);
    }

    if (sourceFilter === "unrecorded") {
      filtered = filtered.filter((b) => !b.bookingSource);
    } else if (sourceFilter !== "all") {
      filtered = filtered.filter((b) => b.bookingSource === sourceFilter);
    }

    if (searchTerm.trim() !== "") {
      const term = searchTerm.toLowerCase().trim();
      filtered = filtered.filter((b) => {
        const bookingRef = (
          b.bookingReference ||
          b._id?.slice(-6).toUpperCase() ||
          ""
        ).toLowerCase();
        const customerName = (b.customerName || "").toLowerCase();
        return bookingRef.includes(term) || customerName.includes(term);
      });
    }

    setFilteredBookings(filtered);
  }, [bookings, statusFilter, oasisFilter, sourceFilter, searchTerm]);

  const showConfirmationModal = (
    title,
    message,
    onConfirm,
    confirmText = "Confirm",
    cancelText = "Cancel",
  ) => {
    setConfirmationModal({
      isOpen: true,
      title,
      message,
      onConfirm: async () => {
        try {
          if (onConfirm) await onConfirm();
        } finally {
          setConfirmationModal((prev) => ({ ...prev, isOpen: false }));
        }
      },
      confirmText,
      cancelText,
    });
  };

  const handleOpenModal = (booking = null) => {
    if (booking) {
      setEditingBooking(booking);
    } else {
      setEditingBooking(null);
    }
    setShowBookingForm(true);
  };

  const handleCancel = async (id) => {
    showConfirmationModal(
      "Cancel Booking",
      "Are you sure you want to cancel this booking?",
      async () => {
        try {
          await adminApi.updateBookingStatus(id, "Cancelled");
          fetchBookings();
          showConfirmationModal(
            "Success",
            "Booking cancelled successfully!",
            null,
            "OK",
          );
        } catch (error) {
          console.error("Error cancelling booking:", error);
          showConfirmationModal(
            "Error",
            "Error cancelling booking",
            null,
            "OK",
          );
        }
      },
      "Yes, Cancel",
      "No, Go Back",
    );
  };

  const handleComplete = async (id) => {
    showConfirmationModal(
      "Mark as Completed",
      "Has the customer completed their stay?",
      async () => {
        try {
          await adminApi.updateBookingStatus(id, "Completed");
          fetchBookings();
          showConfirmationModal(
            "Success",
            "Booking marked as completed!",
            null,
            "OK",
          );
        } catch (error) {
          console.error("Error completing booking:", error);
          showConfirmationModal(
            "Error",
            "Error marking booking as completed",
            null,
            "OK",
          );
        }
      },
      "Yes, Complete",
      "Cancel",
    );
  };

  const handleOpenPaymentVerification = (booking) => {
    setSelectedBookingForVerification(booking);
    setVerificationModalOpen(true);
  };

  const handleVerifyPayment = async (bookingId, finalPaymentMethod) => {
    try {
      const booking = bookings.find((b) => b._id === bookingId);
      await adminApi.verifyPayment(bookingId, finalPaymentMethod);

      fetchBookings();
      const message =
        booking.paymentStatus === "Partial"
          ? "Final payment verified successfully!"
          : "Payment verified successfully! Booking confirmed.";
      showConfirmationModal("Success", message, null, "OK");
    } catch (error) {
      console.error("Error verifying payment:", error);
      showConfirmationModal(
        "Error",
        "Error verifying payment: " + error.message,
        null,
        "OK",
      );
      throw error;
    }
  };

  const handleMarkAsFullyPaid = async (id) => {
    showConfirmationModal(
      "Mark as Fully Paid",
      "Has the customer paid the remaining balance?",
      async () => {
        try {
          await adminApi.updatePaymentStatus(id, "Paid");
          fetchBookings();
          showConfirmationModal(
            "Success",
            "Booking marked as fully paid!",
            null,
            "OK",
          );
        } catch (error) {
          console.error("Error marking as fully paid:", error);
          showConfirmationModal(
            "Error",
            "Error marking as fully paid",
            null,
            "OK",
          );
        }
      },
      "Yes, Mark as Paid",
      "Cancel",
    );
  };

  const handleRejectPayment = async (bookingId) => {
    showConfirmationModal(
      "Reject Payment",
      "Are you sure you want to reject this payment?",
      async () => {
        try {
          await adminApi.updatePaymentStatus(bookingId, "Rejected");
          fetchBookings();
          showConfirmationModal(
            "Payment Rejected",
            "Payment has been rejected.",
            null,
            "OK",
          );
        } catch (error) {
          console.error("Error rejecting payment:", error);
          showConfirmationModal(
            "Error",
            "Error rejecting payment: " + error.message,
            null,
            "OK",
          );
        }
      },
      "Yes, Reject",
      "Cancel",
    );
  };

  const handleBookingCreated = () => {
    setShowBookingForm(false);
    setEditingBooking(null);
    fetchBookings();
    showConfirmationModal(
      "Success",
      editingBooking
        ? "Booking updated successfully!"
        : "Booking created successfully!",
      null,
      "OK",
    );
  };

  const getBalance = (booking) => {
    if (!booking) return 0;
    if (booking.paymentType === "fullpayment") return 0;
    return (booking.totalAmount || 0) - (booking.downpayment || 0);
  };

  const getSessionDisplay = (session) => {
    if (!session) return "N/A";
    const sessionMap = {
      Day: "Day (8AM - 5PM)",
      Night: "Night (6PM - 6AM)",
      "22hrs": "22-Hour Session (Flexible)",
    };
    return sessionMap[session] || session;
  };

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const PAGE_SIZE = 10;

  const totalPages = Math.max(1, Math.ceil(filteredBookings.length / PAGE_SIZE));

  useEffect(() => {
    // reset to first page when filters/search change
    setCurrentPage(1);
  }, [statusFilter, oasisFilter, sourceFilter, searchTerm]);

  const handleCheckIn = async (id) => {
    showConfirmationModal(
      "Check-in Customer",
      "Confirm that the customer has arrived at the resort. This will mark the booking as Checked-in.",
      async () => {
        try {
          await adminApi.checkIn(id);
          fetchBookings();
          showConfirmationModal(
            "Success",
            "Customer checked in successfully!",
            null,
            "OK",
          );
        } catch (error) {
          console.error("Error during check-in:", error);
          showConfirmationModal(
            "Error",
            "Check-in failed: " + error.message,
            null,
            "OK",
          );
        }
      },
      "Yes, Check-in",
      "Cancel",
    );
  };

  const handleCheckOut = async (id) => {
    showConfirmationModal(
      "Check-out Customer",
      "Confirm that the customer has left the resort. Any remaining balance will be collected on-site. This will complete the booking and record the sale.",
      async () => {
        try {
          await adminApi.checkOut(id);
          fetchBookings();
          showConfirmationModal(
            "Success",
            "Customer checked out. Booking completed!",
            null,
            "OK",
          );
        } catch (error) {
          console.error("Error during check-out:", error);
          showConfirmationModal(
            "Error",
            "Check-out failed: " + error.message,
            null,
            "OK",
          );
        }
      },
      "Yes, Check-out",
      "Cancel",
    );
  };

  const handleConfirmFullyPaid = async (id) => {
    showConfirmationModal(
      "Confirm Fully Paid",
      "Confirm that the customer has paid the remaining balance online. The booking will be marked as Fully Paid.",
      async () => {
        try {
          await adminApi.verifyPayment(id);
          fetchBookings();
          showConfirmationModal(
            "Success",
            "Booking marked as Fully Paid!",
            null,
            "OK",
          );
        } catch (error) {
          console.error("Error confirming full payment:", error);
          showConfirmationModal(
            "Error",
            "Error confirming payment: " + error.message,
            null,
            "OK",
          );
        }
      },
      "Yes, Confirm Paid",
      "Cancel",
    );
  };

  // Returns true when today >= the booking date (check-in is allowed).
  // Compares calendar dates only (ignores time) so a booking for "today" is
  // immediately check-in-able regardless of what hour the admin opens the page.
  const isBookingDateReached = (bookingDate) => {
    if (!bookingDate) return true; // if no date, don't block
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const arrival = new Date(bookingDate);
    arrival.setHours(0, 0, 0, 0);
    return today >= arrival;
  };

  // ── Action button logic ──────────────────────────────────────────────────
  //
  //  Status       │ Payment  │ paymentType  │ Buttons
  //  ─────────────┼──────────┼──────────────┼──────────────────────────────
  //  Pending      │ Pending  │ downpayment  │ Verify Downpayment · Cancel
  //  Pending      │ Pending  │ fullpayment  │ Verify Full Payment · Cancel
  //  Confirmed    │ Partial  │ —            │ Verify Final Payment (modal)
  //  Confirmed    │ Paid     │ —            │ Check-in (date-gated)
  //  Checked-in   │ Paid     │ —            │ Check-out
  //  Completed    │ –        │ —            │ (none)
  //  Cancelled    │ –        │ —            │ (none)
  //
  const getActions = (booking) => {
    const actions = [];
    const { status, paymentStatus } = booking;

    // Terminal states — no actions
    if (status === "Cancelled" || status === "Completed") return actions;

    // ── STEP 1: Verify Downpayment ───────────────────────────────────────
    // Booking is Pending — customer submitted payment proof. Admin reviews
    // it in the modal and clicks Verify → status becomes Confirmed, paymentStatus Partial.
    // CHANGE TO:
    if (status === "Pending") {
      // Check if this is a full payment booking
      const buttonLabel =
        booking.paymentType === "fullpayment"
          ? "Verify Full Payment"
          : "Verify Downpayment";

      actions.push({
        label: buttonLabel,
        icon: "✓",
        onClick: () => handleOpenPaymentVerification(booking),
        className: "btn-outline-success",
      });
    }

    // ── STEP 2 (optional): Verify Final Payment ─────────────────────────
    // Booking is Confirmed + Partial: customer uploaded proof of remaining balance.
    // Admin must see the proof in the modal before verifying — never call API directly.
    // → opens PaymentVerificationModal; modal's Verify button calls handleVerifyPayment
    //   which hits verifyPayment on the backend (handles Partial → Paid transition).
    if (status === "Confirmed" && paymentStatus === "Partial") {
      actions.push({
        label: "Verify Final Payment",
        icon: "💳",
        onClick: () => handleOpenPaymentVerification(booking),
        className: "btn-outline-success",
      });
    }

    // ── STEP 3: Check-in ─────────────────────────────────────────────────
    // Only allowed when paymentStatus is Paid AND on/after the booking date.
    // Prevents check-in while the remaining balance is still unverified.
    if (status === "Confirmed" && paymentStatus === "Paid") {
      const canCheckIn = isBookingDateReached(booking.bookingDate);
      actions.push({
        label: "Check-in",
        icon: "🏨",
        onClick: canCheckIn ? () => handleCheckIn(booking._id) : undefined,
        className: canCheckIn ? "btn-outline-success" : "btn-outline-success btn-disabled",
        disabled: !canCheckIn,
        tooltip: !canCheckIn
          ? `Cannot check in before the booking date (${new Date(booking.bookingDate).toLocaleDateString()})`
          : undefined,
      });
    }

    // ── STEP 4: Check-out ────────────────────────────────────────────────
    // Customer leaves. Moves to Completed, creates Sale record.
    // If payment is still Partial, the controller marks it Paid (on-site collection).
    if (status === "Checked-in") {
      const label =
        paymentStatus === "Partial"
          ? "Check-out (collect ₱" +
            (
              (booking.totalAmount || 0) - (booking.downpayment || 0)
            ).toLocaleString() +
            ")"
          : "Check-out";
      actions.push({
        label,
        icon: "🚪",
        onClick: () => handleCheckOut(booking._id),
        className: "btn-outline-success",
      });
    }

    // ── Cancel ───────────────────────────────────────────────────────────
    // Available for Pending only. Once Confirmed, the booking is committed.
    if (status === "Pending") {
      actions.push({
        label: "Cancel",
        icon: "✕",
        onClick: () => handleCancel(booking._id),
        className: "btn-outline-danger",
      });
    }

    return actions;
  };

  return (
    <div className="management-page">
      <div className="page-header">
        <h1>Bookings</h1>
        <button className="btn-primary" onClick={() => handleOpenModal()}>
          + Add Booking
        </button>
      </div>

      <div className="filter-section">
        <div className="filter-row">
          <div className="filter-group">
            <label>Filter by Status:</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="filter-select"
            >
              <option value="all">All Bookings</option>
              <option value="Pending">Pending</option>
              <option value="Confirmed">Confirmed</option>
              <option value="Checked-in">Checked-in</option>
              <option value="Cancelled">Cancelled</option>
              <option value="Completed">Completed</option>
            </select>
          </div>

          <div className="filter-group">
            <label>Filter by Oasis:</label>
            <select
              value={oasisFilter}
              onChange={(e) => setOasisFilter(e.target.value)}
              className="filter-select"
            >
              <option value="all">All Oases</option>
              <option value="Oasis 1">Oasis 1</option>
              <option value="Oasis 2">Oasis 2</option>
            </select>
          </div>

          <div className="filter-group">
            <label>Booking Source:</label>
            <select
              value={sourceFilter}
              onChange={(e) => setSourceFilter(e.target.value)}
              className="filter-select"
            >
              <option value="all">All Sources</option>
              <option value="Online">Online Reservation</option>
              <option value="Walk-in">Walk-in</option>
              <option value="unrecorded">Source Not Recorded</option>
            </select>
          </div>

          <div className="filter-group search-group">
            <label>Search:</label>
            <div className="search-wrapper">
              <i className="fas fa-search search-icon"></i>
              <input
                type="text"
                placeholder="Search by Booking Reference or Customer Name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
              {searchTerm && (
                <button
                  className="clear-search-btn"
                  onClick={() => setSearchTerm("")}
                >
                  <i className="fas fa-times"></i>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="stats-row">
        <div className="stat-card">
          <h3>Pending</h3>
          <p className="stat-number">
            {filteredBookings.filter((b) => b.status === "Pending").length}
          </p>
        </div>
        <div className="stat-card">
          <h3>Confirmed</h3>
          <p className="stat-number">
            {filteredBookings.filter((b) => b.status === "Confirmed").length}
          </p>
        </div>
        <div className="stat-card">
          <h3>Checked-in</h3>
          <p className="stat-number">
            {filteredBookings.filter((b) => b.status === "Checked-in").length}
          </p>
        </div>
        <div className="stat-card">
          <h3>Completed</h3>
          <p className="stat-number">
            {filteredBookings.filter((b) => b.status === "Completed").length}
          </p>
        </div>
        <div className="stat-card">
          <h3>Cancelled</h3>
          <p className="stat-number">
            {filteredBookings.filter((b) => b.status === "Cancelled").length}
          </p>
        </div>
      </div>

      <div className="bookings-table-container">
        {/* Top pagination / summary */}
        <div className="table-top-controls">
          <div className="showing-summary">
            Showing {filteredBookings.length === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1}
            -{Math.min(currentPage * PAGE_SIZE, filteredBookings.length)} of {filteredBookings.length}
          </div>
          <div className="pagination-controls">
            <button
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            >
              Prev
            </button>
            <span className="page-indicator">{currentPage} / {totalPages}</span>
            <button
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            >
              Next
            </button>
          </div>
        </div>

        <table className="bookings-table">
          <thead>
            <tr>
              <th style={{ width: "150px" }}>Booking Reference</th>
              <th>Customer</th>
              <th>Location</th>
              <th>Booking Source</th>
              <th>Package</th>
              <th>Session</th>
              <th>Booking Date</th>
              <th>Reservation Date</th>
              <th>Payment</th>
              <th>Status</th>
              <th style={{ width: "220px" }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredBookings
              .slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE)
              .map((booking, index) => {
              const actions = getActions(booking);
              const balance = getBalance(booking);

              return (
                <tr key={booking._id}>
                  <td>
                    {booking.bookingReference || booking._id?.slice(-6).toUpperCase()}
                  </td>
                  <td>{booking.customerName}</td>
                  <td>{booking.oasis}</td>
                  <td>{booking.bookingSource === "Online" ? "Online Reservation" : booking.bookingSource || "Not Recorded"}</td>
                  <td>{booking.package}</td>
                  <td>{getSessionDisplay(booking.session)}</td>
                  <td>{booking.createdAt ? new Date(booking.createdAt).toLocaleDateString() : "-"}</td>
                  <td>{booking.bookingDate ? new Date(booking.bookingDate).toLocaleDateString() : "-"}</td>
                  <td>
                    {booking.paymentStatus === "Partial" && balance > 0 ? (
                      <span className="status-badge status-partial">
                        Partial (₱{balance.toLocaleString()} due)
                      </span>
                    ) : (
                      <span
                        className={`status-badge status-${booking.paymentStatus?.toLowerCase() || "pending"}`}
                      >
                        {booking.paymentStatus || "Pending"}
                      </span>
                    )}
                  </td>
                  <td>
                    <span
                      className={`status-badge status-${booking.status?.toLowerCase()}`}
                    >
                      {booking.status}
                    </span>
                  </td>
                  <td className="actions-cell">
                    {actions.map((action, idx) => (
                      <button
                        key={idx}
                        className={action.className}
                        onClick={action.disabled ? undefined : action.onClick}
                        disabled={action.disabled}
                        title={action.tooltip}
                      >
                        <span className="btn-icon">{action.icon}</span>
                        {action.label}
                      </button>
                    ))}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {/* Bottom pagination controls */}
        <div className="table-bottom-controls">
          <div className="showing-summary">
            Showing {filteredBookings.length === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1}
            -{Math.min(currentPage * PAGE_SIZE, filteredBookings.length)} of {filteredBookings.length}
          </div>
          <div className="pagination-controls">
            <button
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            >
              Prev
            </button>
            <span className="page-indicator">{currentPage} / {totalPages}</span>
            <button
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {/* Booking Form Modal */}
      {showBookingForm && (
        <div
          className="modal-overlay"
          onClick={() => setShowBookingForm(false)}
        >
          <div className="modal-container" onClick={(e) => e.stopPropagation()}>
            <AdminBookingForm
              editingBooking={editingBooking}
              onClose={() => setShowBookingForm(false)}
              onBookingCreated={handleBookingCreated}
            />
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={confirmationModal.isOpen}
        title={confirmationModal.title}
        message={confirmationModal.message}
        onConfirm={confirmationModal.onConfirm}
        onClose={() =>
          setConfirmationModal((prev) => ({ ...prev, isOpen: false }))
        }
        confirmText={confirmationModal.confirmText}
        cancelText={confirmationModal.cancelText}
      />

      {/* Payment Verification Modal */}
      <PaymentVerificationModal
        isOpen={verificationModalOpen}
        booking={selectedBookingForVerification}
        onClose={() => {
          setVerificationModalOpen(false);
          setSelectedBookingForVerification(null);
        }}
        onVerify={handleVerifyPayment}
        onReject={handleRejectPayment}
      />
    </div>
  );
};

export default BookingManagement;